import type { EngageTenantUsage } from '@reach/shared-types/engage-tenants';
import React from 'react';
import { Button } from '../../../components/ui/button';
import { EngageTenantRow } from './EngageTenantRow';
import { ENGAGE_TENANTS_PAGE_SIZE } from './useEngageTenants';
import styles from './EngageTenantsTable.module.css';

interface EngageTenantsTableProps {
  data: EngageTenantUsage[];
  periodLabel: string;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  hasNextPage: boolean;
  currentPage: number;
  onNextPage: () => void;
  onPreviousPage: () => void;
}

export const EngageTenantsTable: React.FC<EngageTenantsTableProps> = ({
  data,
  periodLabel,
  isLoading,
  isFetching,
  error,
  hasNextPage,
  currentPage,
  onNextPage,
  onPreviousPage,
}) => {
  if (isLoading) {
    return (
      <div className={styles.loading}>
        <div className={styles.loadingPulse}>
          <div className={`${styles.loadingBar} ${styles.loadingBarShort}`} />
          <div className={`${styles.loadingBar} ${styles.loadingBarFull}`} />
          <div className={`${styles.loadingBar} ${styles.loadingBarFull}`} />
          <div className={`${styles.loadingBar} ${styles.loadingBarFull}`} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.errorTitle}>Error loading Engage tenants</div>
        <div className={styles.errorMessage}>{error.message || 'An unexpected error occurred'}</div>
      </div>
    );
  }

  if (data.length === 0) {
    return <div className={styles.empty}>No tenants are using Engage yet.</div>;
  }

  const startRank = (currentPage - 1) * ENGAGE_TENANTS_PAGE_SIZE + 1;

  return (
    <div className={styles.tableWrapper}>
      <div className={styles.tableScroll}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>#</th>
              <th>Tenant Name</th>
              <th>Emails Sent ({periodLabel})</th>
              <th>SMS Sent ({periodLabel})</th>
              <th>Active Automations (Current)</th>
              <th>SMS Numbers (Current)</th>
            </tr>
          </thead>
          <tbody>
            {data.map((tenant, index) => (
              <EngageTenantRow key={tenant.tenantId} rank={startRank + index} tenant={tenant} />
            ))}
          </tbody>
        </table>
      </div>

      <div className={styles.pagination}>
        <div className={styles.pageInfo}>
          Showing {startRank}–{startRank + data.length - 1}
        </div>
        <div className={styles.pageControls}>
          <Button
            variant="outline"
            size="sm"
            onClick={onPreviousPage}
            disabled={currentPage === 1 || isFetching}>
            Previous
          </Button>
          <span>Page {currentPage}</span>
          <Button variant="outline" size="sm" onClick={onNextPage} disabled={!hasNextPage || isFetching}>
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};
